// src/utils/auth.ts

import {
    getAuthUserId,
    getAuthRole,
    getAuthTermsAccepted,
    getGoogleConnected,
    getAuthManageStudentAthletesEnabled,
    clearAuthSnapshot,
} from "./authStore";
import { persistLoginNext, sanitizeLoginNext } from "./loginRedirect";
import { clearAthleteCollegeNameCache } from "../hooks/athleteCollegeListCache";
import { clearAssistAccessCache } from "../hooks/assistAccessCache";

/**
 * IMPORTANT: JWT tokens are now stored in HTTP-only cookies (SESSION_TOKEN)
 * and are NOT accessible from JavaScript. This is a security feature.
 * We only store non-sensitive user metadata in localStorage.
 */

/**
 * @deprecated JWT tokens are now in HTTP-only cookies, not localStorage
 * This function is kept for backward compatibility but should not be used
 */
export const TOKEN_KEY = "jwt";

/**
 * @deprecated Use userId from localStorage instead
 */
export function getToken(): string | null {
    return null; // Tokens are in HTTP-only cookies, not accessible from JS
}

/**
 * @deprecated Tokens are managed by backend via cookies
 */
export function setToken(t: string) {
    // No-op: tokens are in HTTP-only cookies
}

/**
 * @deprecated Tokens are managed by backend via cookies
 * However, this clears any non-HTTP-only cookies as a fallback
 */
export function clearToken() {
    // HTTP-only cookies can only be cleared by backend
    // But we can clear any non-HTTP-only cookies as a fallback
    try {
        document.cookie.split(";").forEach((c) => {
            const cookieName = c.split("=")[0].trim();
            // Clear for current domain
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
            // Clear for parent domain
            const domain = window.location.hostname;
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
            // Clear for parent domain with dot
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${domain};`;
        });
    } catch (error) {
        // Ignore errors
    }
}

/**
 * @deprecated JWT parsing not needed since tokens are in HTTP-only cookies
 */
export function parseJwt<T = any>(token?: string | null): T | null {
    return null;
}

/**
 * @deprecated Token expiry is handled by backend
 */
export function isTokenExpired(token?: string | null): boolean {
    return false; // Backend handles token expiry and returns 401
}

export function getUserId(): string | null {
    // Prefer the in-memory snapshot. Fall back to localStorage.
    const fromStore = getAuthUserId();
    if (fromStore != null) {
        return String(fromStore);
    }

    const userId = localStorage.getItem("userId");

    if (userId && userId.trim() && userId !== "0" && userId !== "null" && userId !== "undefined") {
        return userId.trim();
    }

    return null;
}

/**
 * Gets user role from localStorage
 * Note: Role information should be set during login/OAuth callback
 * The backend validates roles on each request using the JWT cookie
 */
/**
 * True when the user has linked Google (Drive/Calendar). Prefers the in-memory
 * snapshot from /api/auth/check.
 */
export function isSignedInWithGoogle(): boolean {
    if (getGoogleConnected()) {
        return true;
    }
    return localStorage.getItem("authProvider") === "google";
}

export function getUserRole(): string | null {
    // Prefer the in-memory snapshot. Fall back to localStorage.
    const fromStore = getAuthRole();
    if (fromStore) {
        return fromStore;
    }
    return localStorage.getItem('userRole');
}

export function isAthlete(): boolean {
    const role = getUserRole();

    if (!role) return false;

    const lowerRole = role.toLowerCase();
    return lowerRole === 'athlete' ||
           lowerRole === 'student' ||
           lowerRole.includes('athlete') ||
           lowerRole.includes('student');
}

export function isStaff(): boolean {
    const role = getUserRole();
    if (!role) return false;

    const lowerRole = role.toLowerCase();
    return lowerRole === 'staff' || lowerRole.includes('staff');
}

export function isCoach(): boolean {
    const role = getUserRole();
    if (!role) return false;
    return role.toLowerCase() === 'coach';
}

export function isAdmin(): boolean {
    const role = getUserRole();
    if (!role) return false;

    const lowerRole = role.toLowerCase();
    return lowerRole === 'admin' ||
           lowerRole === 'administrator' ||
           lowerRole.includes('admin');
}

export function isGuardian(): boolean {
    const role = getUserRole();
    if (!role) return false;

    const lowerRole = role.toLowerCase();
    return lowerRole === 'guardian' || lowerRole.includes('guardian');
}

export function isPolaris(): boolean {
    const role = getUserRole();
    if (!role) return false;

    const upperRole = role.toUpperCase();
    return upperRole === 'POLARIS' || upperRole.includes('POLARIS');
}

/** School-side adults who use the admin/staff shell (not athletes or guardians). */
export function isSchoolAdult(): boolean {
    return isAdmin() || isStaff() || isCoach();
}

/** Admin/Staff always; Coach only when Add & Manage Student-Athletes is enabled. */
export function canManageAthletes(): boolean {
    if (isAdmin() || isStaff() || isPolaris()) {
        return true;
    }
    return isCoach() && getAuthManageStudentAthletesEnabled();
}

/** School Instant Chat is only for administrators, staff, and coaches at the same company. */
export function canShowTeamChat(): boolean {
    if (isGuardian() || isPolaris() || isAthlete()) {
        return false;
    }
    return isSchoolAdult();
}

/**
 * True when the user signed in with Clever and has a school staff or administrator role
 * (excludes Clever students/athletes).
 */
export function isCleverStaffOrAdministrator(
    authProvider: string | null | undefined,
    role: string | null
): boolean {
    if (!authProvider || authProvider.toLowerCase() !== "clever") {
        return false;
    }
    if (!role) {
        return false;
    }
    const lowerRole = role.toLowerCase();
    return (
        lowerRole === "staff" ||
        lowerRole === "admin" ||
        lowerRole === "administrator" ||
        lowerRole.includes("staff") ||
        lowerRole.includes("admin")
    );
}

export function canEditProfile(profileUserId?: string): boolean {
    // Administrators can edit anyone's profile
    if (isAdmin()) {
        return true;
    }

    // Staff and other users can only edit their own profile.
    // Prefer the in-memory snapshot.
    const fromStore = getAuthUserId();
    const currentUserId = fromStore != null ? String(fromStore) : localStorage.getItem("loggedInUserId");
    return currentUserId === profileUserId ||
           (!profileUserId && true); // Allow editing own profile when no specific user ID
}

/** Auth-related localStorage keys cleared on logout / forced re-login. */
const AUTH_STORAGE_KEYS = [
    'userId',
    'loggedInUserId',
    'hasProfile',
    'termsAndConditionsAccepted',
    'authProvider',
    'hasLoggedInBefore',
    'userRole',
    'guardianAthletes',
    'guardianViewingAthleteId',
    'requirePasswordChange',
];

/**
 * Clears client-side auth metadata: any non-HTTP-only cookies plus the auth-related
 * localStorage keys. The HTTP-only session cookie can only be cleared by the backend.
 */
export function clearAuthStorage() {
    clearToken();
    AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
    clearAssistAccessCache();
    clearAthleteCollegeNameCache();
}

export function redirectToLogin(preserve?: boolean) {
    const loc = window.location;
    const current = preserve ? loc.pathname + loc.search + loc.hash : "";

    clearAuthStorage();

    let to = "/login";
    if (preserve) {
        const next = persistLoginNext(current) ?? sanitizeLoginNext(current);
        if (next) {
            to = `/login?next=${encodeURIComponent(next)}`;
        }
    }
    // Hard redirect (works outside React tree)
    window.location.replace(to);
}

/**
 * Forces a clean re-login after we detect this browser's session cookie no longer
 * matches the user this tab is showing — e.g. another account signed in via a shared
 * cookie jar (common with multiple Chrome incognito windows, which share cookies), or
 * the session expired. Clears local auth state and redirects to /login with a reason
 * the login screen can explain, instead of leaving the user on a cryptic error.
 */
export function forceReauth(reason: "session_changed" | "session_expired") {
    clearAuthStorage();
    // Also drop the in-memory identity snapshot so nothing stale lingers before the
    // hard redirect reloads the page.
    try {
        clearAuthSnapshot();
    } catch {
        /* no-op */
    }
    window.location.replace(`/login?${reason}=true`);
}

// Terms and Conditions utilities
export function getTermsAccepted(): boolean {
    // Prefer the in-memory snapshot. Fall back to localStorage.
    if (getAuthTermsAccepted()) {
        return true;
    }
    const accepted = localStorage.getItem('termsAndConditionsAccepted');
    return accepted === 'true';
}

export function setTermsAccepted(accepted: boolean) {
    localStorage.setItem('termsAndConditionsAccepted', String(accepted));
}

export function clearTermsAccepted() {
    localStorage.removeItem('termsAndConditionsAccepted');
}

// Profile status utilities
export function getHasProfile(): boolean {
    const hasProfile = localStorage.getItem('hasProfile');
    return hasProfile === 'true';
}

export function setHasProfile(hasProfile: boolean) {
    localStorage.setItem('hasProfile', String(hasProfile));
}

export function clearHasProfile() {
    localStorage.removeItem('hasProfile');
}

